import type { AlbumLibraryEntry, DeepDive } from '@/types/album';

export const library = {
  id: 'ok-computer',
  artist: 'Radiohead',
  album: 'OK Computer',
  year: 1997,
  genre: 'Rock alternativo',
  hook: 'Una sobrecarga de sistema que aprende a alejarse de la maquinaria.',
  palette: { bg: '#1A1F26', fg: '#E8DCC4', accent: '#9B7EC8' },
  typeStyle: 'stencil',
  available: true,
  hero: {
    apparent: 'un gran disco-manifiesto noventero',
    key: 'una sobrecarga de sistema',
    coda: 'que poco a poco aprende a alejarse de la maquinaria.',
  },
  deepDiveLine: {
    apparent: 'una gran declaración de época',
    key: 'un sistema que primero acelera la ansiedad y luego deja al oyente dentro de sus ruinas',
  },
  sectionTitle: {
    pre: 'OK Computer, leído como una',
    em: 'sobrecarga de sistema',
    post: 'que aprende a alejarse de la maquinaria.',
  },
  songsTitle: 'Tres canciones, leídas con cuidado.',
  footerCredit: 'Análisis original · OK Computer · Radiohead · 1997',
  coverImage: '/images/ok-computer.jpg',
} as const satisfies AlbumLibraryEntry;

export const deepDive: DeepDive = {
  artist: 'Radiohead',
  title: 'OK Computer',
  year: '1997',
  position_label: 'Tercer álbum de estudio · Ruptura de consolidación',
  apparent_form:
    'Un álbum de rock alternativo grande y ambicioso, muy secuenciado, con aura de declaración generacional y varios temas de inmediato reconocibles.',
  actual_behavior:
    'En secuencia es una escalada de saturación nerviosa que cruza varios climas y luego cae en una especie de shock. Los primeros seis temas todavía tienen forma de canciones de rock expansivas, "Fitter Happier" parte el disco en dos, y desde ahí todo se vuelve más físico y más desorientado hasta terminar en una retirada cansada con "The Tourist".',
  form_behavior_gap:
    'Parece una gran declaración de época; al escucharlo de corrido, se comporta como un sistema que primero acelera la ansiedad y luego deja al oyente dentro de sus ruinas.',
  previous_context:
    '"The Bends" había afinado a Radiohead como una banda de guitarras emocional, melódica y todavía bastante terrestre. Aquí amplían la escala, aflojan la lógica de sencillo tradicional y convierten el estudio en parte central del discurso.',
  next_context:
    'Llegó "Kid A", que toma la paranoia tecnológica y la disolución de identidad de "OK Computer" y las lleva a un lenguaje mucho menos guitarrero y mucho más abstracto.',
  previous_album: { title: 'The Bends', year: 1995 },
  next_album: { title: 'Kid A', year: 2000 },
  position_summary:
    'El punto donde Radiohead deja de ser una gran banda británica de los noventa y se convierte en una referencia de largo plazo. Una ruptura, sí, pero construida desde habilidades ya presentes en "The Bends": melodía fuerte, ansiedad intensa y ambición formal, ahora llevadas a un marco mucho más raro y mucho más total.',

  arch_type: 'Giro central',
  arch_summary:
    'El disco está construido como una secuencia con bisagra. Primero acumula presión dentro de canciones todavía abiertas y memorables; después del corte de "Fitter Happier", la experiencia se vuelve más estrecha, más clínica y más cansada hasta llegar a una salida en cámara lenta.',
  arch_opening:
    '"Airbag" abre con un riff fragmentado y un ritmo recortado que ya mezclan euforia y daño, como una reanimación. "Paranoid Android" confirma desde muy temprano que el disco no va a sostener un solo modo.',
  arch_middle:
    'El centro real está entre "Let Down", "Karma Police" y "Fitter Happier". Pasa de la melancolía expansiva a un diagnóstico más frío: muestra el daño emocional, ofrece una falsa canción de orden, y después corta la circulación con una pieza sin cuerpo humano.',
  arch_closing:
    'El cierre no busca explosión ni redención plena. "Lucky" ofrece una última elevación casi mesiánica, pero "The Tourist" descomprime el pulso y termina en una lentitud agotada, como si el único gesto posible fuera retirarse del flujo por un momento.',
  arch_sequencing_note:
    'La colocación de "Fitter Happier" en el séptimo puesto parte el álbum justo cuando parecía haberse asentado en una lógica de grandes canciones, y convierte todo lo que sigue en secuela de ese quiebre.',

  tensions: [
    {
      label: 'Seguir vivo vs sentirse ya deshumanizado',
      description:
        'El disco vuelve una y otra vez a personajes que siguen vivos, pero no de un modo celebratorio. En "Airbag" el regreso tiene brillo y adrenalina, como si el cuerpo fuese ya una máquina remendada; en "Lucky" la suerte suena más arbitraria que redentora. Cuando llega "No Surprises", la fantasía de una vida sin sobresaltos ya suena menos a paz que a anestesia.',
      anchors: [
        { track: 'Airbag', note: 'accidente convertido en renacimiento mecánico' },
        { track: 'Lucky', note: 'supervivencia tratada como rareza casi absurda' },
        { track: 'No Surprises', note: 'calma doméstica cantada como fuga química' },
      ],
    },
    {
      label: 'Querer orden moral vs vivir dentro de sistemas ya rotos',
      description:
        'Muchas canciones buscan orden, castigo o una línea clara de conducta, pero la música siempre mete ruido dentro de ese deseo. "Karma Police" ofrece la ilusión de que alguien todavía puede poner las cosas en su sitio, y por eso pega tan fuerte; el álbum lleva rato mostrando que los sistemas de control ya están corroídos. "Paranoid Android" y "Electioneering" convierten la vida pública en un teatro histérico donde todo impulso de corrección llega tarde o llega deformado.',
      anchors: [
        { track: 'Paranoid Android', note: 'cambios de sección que impiden cualquier estabilidad' },
        { track: 'Karma Police', note: 'juicio moral en una canción casi himno' },
        { track: 'Electioneering', note: 'energía de campaña convertida en ruido oportunista' },
      ],
    },
    {
      label: 'Necesitar contacto vs retirarse de lo humano común',
      description:
        'El álbum está lleno de voces que quieren ser vistas y rescatadas, pero casi nunca soportan la cercanía real. "Subterranean Homesick Alien" sueña con una mirada extraterrestre porque la humana ya no alcanza; "Let Down" observa a la multitud como si la conexión social fuese otra cinta transportadora. El cierre con "The Tourist" ofrece una forma mínima de cuidado, casi un recordatorio corporal, aunque llega después de mucho daño acumulado.',
      anchors: [
        {
          track: 'Subterranean Homesick Alien',
          note: 'fantasía de contacto con algo externo a la vida social',
        },
        { track: 'Let Down', note: 'gente triturada por infraestructura y rutina' },
        { track: 'The Tourist', note: '"hey man, slow down" como gesto final de rescate' },
      ],
    },
  ],

  signature_moments: [
    {
      track: 'Fitter Happier',
      anchor: 'la entrada de la voz sintética enumerando rutinas de bienestar',
      pull: 'Después de oírlo, las canciones que siguen ya no parecen confesiones nerviosas: suenan como vidas acomodadas a la fuerza dentro de un sistema de eficiencia, vigilancia y autooptimización.',
      why: 'Ese corte convierte la ansiedad difusa de la primera mitad en un lenguaje explícito de gestión humana.',
    },
    {
      track: 'The Tourist',
      anchor: 'el tintineo final y la orden "hey man, slow down"',
      pull: 'El disco entero viene empujando hacia la sobrecarga, así que cerrar con una advertencia tan simple cambia la escala de todo. De pronto deja de sonar como pura profecía tecnológica y se vuelve un mensaje corporal.',
      why: 'Baja la velocidad o te conviertes en otro ruido dentro de la máquina.',
    },
  ],

  motifs: [
    'Máquinas, transporte e infraestructura como marco de la vida emocional aparecen en "Airbag", "Let Down", "Karma Police" y "The Tourist".',
    'Voces de control, instrucción o vigilancia atraviesan "Karma Police", "Fitter Happier", "Climbing Up the Walls" y "No Surprises".',
    'La fantasía de escapar de la vida social ordinaria recorre "Subterranean Homesick Alien", "Exit Music (for a Film)", "Lucky" y "The Tourist".',
  ],

  enduring:
    '"OK Computer" ha durado porque captó una forma de ansiedad moderna sin encerrarla en consignas ni en una sola lectura histórica. Sigue sonando actual porque no habla solo de tecnología, habla de cómo una persona se va volviendo gestionable, intercambiable y cansada mientras todavía intenta sentir algo verdadero. Mucha gente lo recuerda como un gran disco sobre el fin de siglo; vuelve una y otra vez porque, más cerca del oído, es un disco sobre qué le pasa al cuerpo cuando el mundo se acelera más de lo que uno puede procesar.',

  tracks: [
    { n: 1, title: 'Airbag', ms: 284400 },
    { n: 2, title: 'Paranoid Android', ms: 384000, hasMeaning: true },
    { n: 3, title: 'Subterranean Homesick Alien', ms: 267706 },
    { n: 4, title: 'Exit Music (for a Film)', ms: 264800 },
    { n: 5, title: 'Let Down', ms: 299266 },
    { n: 6, title: 'Karma Police', ms: 262440, hasMeaning: true },
    { n: 7, title: 'Fitter Happier', ms: 117333, hasMeaning: true, hinge: true },
    { n: 8, title: 'Electioneering', ms: 230640 },
    { n: 9, title: 'Climbing Up the Walls', ms: 285200 },
    { n: 10, title: 'No Surprises', ms: 228533 },
    { n: 11, title: 'Lucky', ms: 259626 },
    { n: 12, title: 'The Tourist', ms: 324533 },
  ],

  songs_with_meaning: ['Paranoid Android', 'Karma Police', 'Fitter Happier'],
};
